import React, { useState, useEffect } from 'react';
import { X, History, Clock, ArrowRight } from 'lucide-react';

const FeeHistoryDrawer = ({
  isOpen,
  onClose,
  student,
  feeService,
  orgId
}) => {
  const [activeTab, setActiveTab] = useState('payments'); // 'payments' | 'audit'
  const [payments, setPayments] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && student?.studentId) {
      setLoading(true);
      Promise.all([
        feeService.getTransactionHistory(student.studentId, orgId).catch(() => []),
        feeService.getFeeHistory(student.studentId, orgId).catch(() => [])
      ])
        .then(([txs, history]) => {
          setPayments(Array.isArray(txs) ? txs : []);
          setAuditLogs(Array.isArray(history) ? history : []);
        })
        .finally(() => setLoading(false));
    }
  }, [isOpen, student?.studentId, feeService, orgId]);

  if (!isOpen || !student) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.45)',
        backdropFilter: 'blur(3px)',
        zIndex: 1050,
        display: 'flex',
        justifyContent: 'flex-end'
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '460px',
          height: '100%',
          backgroundColor: '#ffffff',
          boxShadow: '-4px 0 24px rgba(0, 0, 0, 0.15)',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 1051
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: '#f8fafc'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '6px',
                background: '#eff6ff',
                color: '#2563eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <History size={18} strokeWidth={2.2} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '800', color: '#0f172a' }}>
                Fee & Payment History
              </h3>
              <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#64748b' }}>
                {student.name} ({student.studentId})
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#64748b',
              cursor: 'pointer',
              padding: '6px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Tab Toggle */}
        <div
          style={{
            display: 'flex',
            borderBottom: '1px solid #e2e8f0',
            background: '#f8fafc',
            padding: '4px 16px 0 16px',
            gap: '8px'
          }}
        >
          <button
            type="button"
            onClick={() => setActiveTab('payments')}
            style={{
              padding: '8px 12px',
              fontSize: '12px',
              fontWeight: '700',
              border: 'none',
              background: 'transparent',
              borderBottom: activeTab === 'payments' ? '2px solid #2563eb' : '2px solid transparent',
              color: activeTab === 'payments' ? '#2563eb' : '#64748b',
              cursor: 'pointer'
            }}
          >
            💳 Payments & Receipts ({payments.length})
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('audit')}
            style={{
              padding: '8px 12px',
              fontSize: '12px',
              fontWeight: '700',
              border: 'none',
              background: 'transparent',
              borderBottom: activeTab === 'audit' ? '2px solid #2563eb' : '2px solid transparent',
              color: activeTab === 'audit' ? '#2563eb' : '#64748b',
              cursor: 'pointer'
            }}
          >
            🛡️ Fee Audit Trail ({auditLogs.length})
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b', fontSize: '13px' }}>
              Loading historical logs...
            </div>
          ) : activeTab === 'payments' ? (
            payments.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8', border: '1px dashed #cbd5e1', borderRadius: '8px' }}>
                No payment transactions recorded for this student.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {payments.map((tx, idx) => (
                  <div
                    key={tx.transactionId || idx}
                    style={{
                      background: '#ffffff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      padding: '12px 14px',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontWeight: '700', color: '#0f172a', fontSize: '13px' }}>
                        Receipt #{tx.receiptNumber || tx.transactionId?.slice(-8) || 'RCPT'}
                      </span>
                      <span style={{ fontWeight: '800', color: '#166534', fontSize: '15px' }}>
                        ₹{Number(tx.paidAmount || 0).toLocaleString('en-IN')}
                      </span>
                    </div>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '6px', fontSize: '11px', color: '#64748b' }}>
                      <span>📅 {tx.paymentDate ? new Date(tx.paymentDate).toLocaleDateString('en-IN') : 'N/A'}</span>
                      <span>•</span>
                      <span>💳 {tx.paymentMode || 'Cash'}</span>
                      {tx.collectedByName && (
                        <>
                          <span>•</span>
                          <span>👤 Collected by: {tx.collectedByName}</span>
                        </>
                      )}
                    </div>

                    {tx.remarks && (
                      <div style={{ marginTop: '6px', fontSize: '11px', color: '#334155', background: '#f8fafc', padding: '4px 8px', borderRadius: '4px' }}>
                        Note: {tx.remarks}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )
          ) : (
            auditLogs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8', border: '1px dashed #cbd5e1', borderRadius: '8px' }}>
                No fee corrections or modifications recorded.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {auditLogs.map((log, idx) => (
                  <div
                    key={log.historyId || idx}
                    style={{
                      background: '#ffffff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      padding: '12px 14px',
                      position: 'relative'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <span style={{ fontSize: '11px', fontWeight: '700', color: '#2563eb', background: '#eff6ff', padding: '2px 6px', borderRadius: '4px' }}>
                        {log.actionType || 'FEE_CORRECTION'}
                      </span>
                      <span style={{ fontSize: '11px', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <Clock size={12} />
                        {log.modifiedAt ? new Date(log.modifiedAt).toLocaleString('en-IN') : 'Recent'}
                      </span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: '700', color: '#0f172a', margin: '6px 0' }}>
                      <span style={{ color: '#64748b', textDecoration: 'line-through' }}>
                        ₹{Number(log.oldTotalFees || 0).toLocaleString('en-IN')}
                      </span>
                      <ArrowRight size={14} color="#94a3b8" />
                      <span style={{ color: '#166534' }}>
                        ₹{Number(log.newTotalFees || 0).toLocaleString('en-IN')}
                      </span>
                    </div>

                    <div style={{ fontSize: '11.5px', color: '#334155', background: '#f8fafc', padding: '6px 8px', borderRadius: '4px', border: '1px solid #f1f5f9' }}>
                      <strong>Reason:</strong> {log.reason || 'Correction applied'}
                    </div>

                    <div style={{ fontSize: '10.5px', color: '#64748b', marginTop: '6px' }}>
                      Modified by: <strong>{log.modifiedByName || log.modifiedBy || 'Administrator'}</strong>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '12px 20px',
            borderTop: '1px solid #e2e8f0',
            background: '#f8fafc',
            display: 'flex',
            justifyContent: 'flex-end'
          }}
        >
          <button
            type="button"
            className="btn btn-light"
            onClick={onClose}
            style={{ fontSize: '12px', fontWeight: '600', padding: '6px 16px', border: '1px solid #cbd5e1' }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeeHistoryDrawer;
